import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useGet } from "../api/useApi";
import { API_ROUTES } from "../api/apiRoutes";
import { IconSearch, IconX } from "../components/Icons";
import useSeo from "../seo/useSeo";
import { ROUTE_META } from "../seo/seoConfig";

const OTHER_LETTER = "#";

const getGroupLetter = (name) => {
  const first = (name || "").trim().charAt(0).toUpperCase();
  return /[A-Z]/.test(first) ? first : OTHER_LETTER;
};

const BrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState("");
  const groupRefs = useRef({});

  const {
    loading: loadingBrands,
    error: brandsError,
    execute: getBrands,
  } = useGet(API_ROUTES.BRAND.GET_OPTIONS, {
    executeOnMount: true,
    onSuccess: (data) => {
      setBrands(Array.isArray(data?.value) ? data.value : []);
    },
    onError: (error) => {
      console.error("Error fetching brands:", error);
    },
  });

  useSeo({
    title: ROUTE_META["/brands"].title,
    description: ROUTE_META["/brands"].description,
    canonicalPath: "/brands",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredBrands = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    const list = keyword
      ? brands.filter((b) => (b?.name || "").toLowerCase().includes(keyword))
      : brands;

    return [...list].sort((a, b) =>
      (a?.name || "").localeCompare(b?.name || "", undefined, {
        sensitivity: "base",
      }),
    );
  }, [brands, search]);

  // { letter: [brand, ...] } in alphabetical order, "#" last
  const groupedBrands = useMemo(() => {
    const groups = {};
    filteredBrands.forEach((brand) => {
      const letter = getGroupLetter(brand?.name);
      if (!groups[letter]) {
        groups[letter] = [];
      }
      groups[letter].push(brand);
    });

    return Object.keys(groups)
      .sort((a, b) => {
        if (a === OTHER_LETTER) return 1;
        if (b === OTHER_LETTER) return -1;
        return a.localeCompare(b);
      })
      .map((letter) => ({ letter, items: groups[letter] }));
  }, [filteredBrands]);

  const availableLetters = useMemo(
    () => new Set(groupedBrands.map((g) => g.letter)),
    [groupedBrands],
  );

  const alphabet = useMemo(() => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    return brands.some((b) => getGroupLetter(b?.name) === OTHER_LETTER)
      ? [...letters, OTHER_LETTER]
      : letters;
  }, [brands]);

  const scrollToLetter = (letter) => {
    const node = groupRefs.current[letter];
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="brands-page">
      {/* Breadcrumb */}
      <div className="all-watches-breadcrumb">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Brands</span>
      </div>

      {/* Page Header */}
      <div className="section-header" style={{ paddingTop: 40 }}>
        <div className="section-label">Maisons We Carry</div>
        <h2 className="section-title">All Brands</h2>
        <div className="section-line" />
        <p className="collection-info">
          {loadingBrands
            ? "Loading brands…"
            : `${brands.length} brand${brands.length !== 1 ? "s" : ""} in our collection`}
        </p>
      </div>

      <div className="brands-container">
        {/* Search */}
        <div className="brands-search">
          <span className="brands-search-icon">
            <IconSearch />
          </span>
          <input
            type="text"
            className="brands-search-input"
            placeholder="Search brands"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search brands"
          />
          {search && (
            <button
              type="button"
              className="brands-search-clear"
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              <IconX />
            </button>
          )}
        </div>

        {/* A–Z quick jump */}
        {!loadingBrands && !search && brands.length > 0 && (
          <div className="brands-alphabet" role="navigation" aria-label="Jump to letter">
            {alphabet.map((letter) => {
              const enabled = availableLetters.has(letter);
              return (
                <button
                  key={letter}
                  type="button"
                  className={`brands-alphabet-item ${enabled ? "" : "disabled"}`}
                  onClick={() => scrollToLetter(letter)}
                  disabled={!enabled}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        )}

        {loadingBrands ? (
          <div className="spinner-container">
            <div className="spinner" />
          </div>
        ) : brandsError && brands.length === 0 ? (
          <div className="all-watches-empty">
            <div className="empty-state-icon-large">⌚</div>
            <div className="empty-state-title-serif">
              Unable to load brands
            </div>
            <div style={{ fontSize: 14 }}>Please try again in a moment</div>
            <button
              className="filter-btn"
              style={{ marginTop: 20 }}
              onClick={() => getBrands()}
            >
              Retry
            </button>
          </div>
        ) : groupedBrands.length > 0 ? (
          groupedBrands.map((group) => (
            <section
              key={group.letter}
              className="brands-group"
              ref={(node) => {
                groupRefs.current[group.letter] = node;
              }}
            >
              <div className="brands-group-letter">
                <span>{group.letter}</span>
                <div className="brands-group-line" />
              </div>
              <div className="brands-grid">
                {group.items.map((brand) => (
                  <Link
                    key={brand?.id}
                    to={`/watches?brand=${encodeURIComponent(brand?.name)}`}
                    className="brand-card"
                  >
                    <span className="brand-card-monogram">
                      {getGroupLetter(brand?.name)}
                    </span>
                    <span className="brand-card-name">{brand?.name}</span>
                    <span className="brand-card-cta">View collection</span>
                  </Link>
                ))}
              </div>
            </section>
          ))
        ) : (
          <div className="all-watches-empty">
            <div className="empty-state-icon-large">⌚</div>
            <div className="empty-state-title-serif">No brands found</div>
            <div style={{ fontSize: 14 }}>
              Nothing matches “{search}”. Try a different spelling.
            </div>
            <button
              className="filter-btn"
              style={{ marginTop: 20 }}
              onClick={() => setSearch("")}
            >
              Clear search
            </button>
          </div>
        )}
      </div>

      <div style={{ height: 80 }} />
    </div>
  );
};

export default BrandsPage;
